import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyMolnarMode({ brand }: ModeRendererProps) {
  // Early computer art: structured randomness and gentle noise
  // Perturbed grids, algorithmic jitter, parametric fields
  
  // Helper: Add gentle random perturbation
  const jitter = (value: number, amount: number = 3) => {
    return value + (Math.random() - 0.5) * amount;
  };
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.paper} />
      
      {/* Perturbed grid - Molnár's signature style */}
      {Array.from({ length: 12 }).map((_, row) =>
        Array.from({ length: 16 }).map((_, col) => {
          const baseX = 80 + col * 40;
          const baseY = 80 + row * 40;
          const jitterAmount = row * 0.8; // Increasing jitter downward
          
          return (
            <rect
              key={`grid-${row}-${col}`}
              x={jitter(baseX, jitterAmount)}
              y={jitter(baseY, jitterAmount)}
              width={jitter(36, jitterAmount * 0.5)}
              height={jitter(36, jitterAmount * 0.5)}
              fill="none"
              stroke={brand.deep}
              strokeWidth="1"
              opacity={0.4}
            />
          );
        })
      )}
      
      {/* Algorithmic concentric squares with gentle noise */}
      {Array.from({ length: 8 }).map((_, i) => {
        const size = 180 - i * 22;
        const offset = i * 11;
        const jitterAmount = i * 2;
        
        return (
          <rect
            key={`concentric-${i}`}
            x={jitter(307 + offset, jitterAmount)}
            y={jitter(620 + offset, jitterAmount)}
            width={jitter(size, jitterAmount * 0.5)}
            height={jitter(size, jitterAmount * 0.5)}
            fill="none"
            stroke={i % 2 === 0 ? brand.violet : brand.aqua}
            strokeWidth="1.5"
            opacity={0.6 - i * 0.05}
          />
        );
      })}
      
      {/* Parametric field of lines with structured randomness */}
      {Array.from({ length: 30 }).map((_, i) => {
        const baseX = 80 + i * 21;
        const baseY1 = 560;
        const baseY2 = 780;
        const phase = i * 0.3;
        
        return (
          <line
            key={`field-${i}`}
            x1={jitter(baseX, 4)}
            y1={jitter(baseY1 + Math.sin(phase) * 20, 5)}
            x2={jitter(baseX, 4)}
            y2={jitter(baseY2 + Math.cos(phase) * 20, 5)}
            stroke={brand.amber}
            strokeWidth="1.5"
            opacity={0.5}
          />
        );
      })}
      
      {/* Grid of circles with systematic perturbation */}
      {Array.from({ length: 8 }).map((_, row) =>
        Array.from({ length: 12 }).map((_, col) => {
          const baseX = 100 + col * 52;
          const baseY = 850 + row * 32;
          const perturbation = (row + col) % 5;
          
          return (
            <circle
              key={`circle-${row}-${col}`}
              cx={jitter(baseX, perturbation * 2)}
              cy={jitter(baseY, perturbation * 2)}
              r={jitter(10, perturbation)}
              fill={
                perturbation % 3 === 0
                  ? brand.aqua
                  : perturbation % 3 === 1
                  ? brand.violet
                  : brand.amber
              }
              opacity={0.4}
            />
          );
        })
      )}
      
      {/* Gentle rotation perturbation on lines */}
      {Array.from({ length: 20 }).map((_, i) => {
        const baseAngle = (i / 20) * Math.PI;
        const angleJitter = (Math.random() - 0.5) * 0.2;
        const angle = baseAngle + angleJitter;
        const cx = 550;
        const cy = 200;
        const r1 = 60;
        const r2 = 120;
        
        return (
          <line
            key={`radial-${i}`}
            x1={cx + Math.cos(angle) * r1}
            y1={cy + Math.sin(angle) * r1}
            x2={cx + Math.cos(angle) * r2}
            y2={cy + Math.sin(angle) * r2}
            stroke={brand.violet}
            strokeWidth="1"
            opacity={0.5}
          />
        );
      })}
      
      {/* Nested squares with progressive jitter */}
      <g transform="translate(150, 230)">
        {Array.from({ length: 6 }).map((_, i) => {
          const size = 120 - i * 18;
          const offset = i * 9;
          const jitterAmount = i * 3;
          
          // Create 4 points for a slightly perturbed square
          const points = [
            `${jitter(offset, jitterAmount)},${jitter(offset, jitterAmount)}`,
            `${jitter(offset + size, jitterAmount)},${jitter(offset, jitterAmount)}`,
            `${jitter(offset + size, jitterAmount)},${jitter(offset + size, jitterAmount)}`,
            `${jitter(offset, jitterAmount)},${jitter(offset + size, jitterAmount)}`,
          ].join(" ");
          
          return (
            <polygon
              key={`perturbed-${i}`}
              points={points}
              fill="none"
              stroke={brand.aqua}
              strokeWidth="1.5"
              opacity={0.5}
            />
          );
        })}
      </g>
    </g>
  );
}
